const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const collectNumericValues = (value, push) => {
  if (isFiniteNumber(value)) {
    push(value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectNumericValues(entry, push));
    return;
  }

  if (value && typeof value === "object") {
    Object.values(value).forEach((entry) => collectNumericValues(entry, push));
  }
};

export const getOutputKeys = (data, renderKey, indicator) => {
  if (indicator?.yExtentKeys && indicator.yExtentKeys.length > 0) {
    return indicator.yExtentKeys;
  }

  if (!data || data.length === 0 || !renderKey) return ["value"];

  const found = data.find((d) => d && d[renderKey]);
  const candidate = found && found[renderKey];

  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return Object.keys(candidate).filter((key) => {
      return data.some((item) => isFiniteNumber(item?.[renderKey]?.[key]));
    });
  }

  return ["value"];
};

export const getIndicatorMinMax = (dataArr, indicator) => {
  if (indicator?.includeInMainExtent === false) {
    return { min: Infinity, max: -Infinity };
  }
  const renderKey = indicator?.renderKey || indicator?.key;
  let min = Infinity;
  let max = -Infinity;

  if (!Array.isArray(dataArr) || !renderKey) {
    return { min, max };
  }

  const outputKeys = getOutputKeys(dataArr, renderKey, indicator);

  const push = (value) => {
    if (!isFiniteNumber(value)) return;
    if (value < min) min = value;
    if (value > max) max = value;
  };

  dataArr.forEach((item) => {
    if (!item) return;
    const entry = item[renderKey];
    if (entry === undefined || entry === null) return;

    // Handle simple numeric values (like ATR, VWAP)
    if (isFiniteNumber(entry)) {
      push(entry);
      return;
    }

    // Handle arrays
    if (Array.isArray(entry)) {
      collectNumericValues(entry, push);
      return;
    }

    // Handle objects
    if (typeof entry === "object") {
      if (outputKeys.length && outputKeys[0] !== "value") {
        // Use specific keys if provided (and not the fallback "value")
        outputKeys.forEach((key) => collectNumericValues(entry[key], push));
      } else {
        // For fallback "value" key or no specific keys, collect all numeric values
        collectNumericValues(entry, push);
      }
    }
  });

  return { min, max };
};

export const getPriceMinMax = (dataArr) => {
  let min = Infinity,
    max = -Infinity;

  dataArr.forEach((item) => {
    if (typeof item.high === "number" && !isNaN(item.high))
      max = Math.max(max, item.high);
    if (typeof item.low === "number" && !isNaN(item.low))
      min = Math.min(min, item.low);
  });

  if (!isFinite(min) || !isFinite(max)) {
    let altMin = Infinity;
    let altMax = -Infinity;
    dataArr.forEach((item) => {
      ["close", "open", "high", "low"].forEach((key) => {
        const value = item?.[key];
        if (typeof value === "number" && !isNaN(value)) {
          if (value < altMin) altMin = value;
          if (value > altMax) altMax = value;
        }
      });
    });

    if (isFinite(altMin) && isFinite(altMax)) {
      min = altMin;
      max = altMax;
    }
  }

  return { min, max };
};
